import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { de } from "date-fns/locale";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BOOKING_TYPES, TYPE_LABELS, TIME_SLOTS } from "@/data/content";

const toLocalISO = (d) => format(d, "yyyy-MM-dd");

export const Booking = () => {
  const [selected, setSelected] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", shoot_type: "hochzeit", time_slot: "flexibel", message: "" });
  const [sending, setSending] = useState(false);
  const [avail, setAvail] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api
      .get("/bookings/booked-dates")
      .then((res) => setBookedDates(res.data.dates.map((s) => new Date(s + "T12:00:00"))))
      .catch(() => {});
    api
      .get("/availability")
      .then((res) => {
        const m = res.data.months.find((x) => x.free > 0);
        if (m) setAvail(m);
      })
      .catch(() => {});
    api
      .get("/promo")
      .then((res) => setPromo(res.data))
      .catch(() => {});
    const onQuiz = (e) => setForm((f) => ({ ...f, shoot_type: e.detail }));
    window.addEventListener("leno:shoot-type", onQuiz);
    return () => window.removeEventListener("leno:shoot-type", onQuiz);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Bitte wählen Sie zuerst ein Datum im Kalender.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/bookings", {
        name: form.name,
        email: form.email,
        date: toLocalISO(selected),
        shoot_type: form.shoot_type,
        time_slot: form.time_slot,
        message: form.message,
      });
      setBookedDates((prev) => [...prev, selected]);
      if (res.data.promo) {
        toast.success("Gratis-Shooting gesichert!", {
          description: "Du hast einen der 3 Gratis-Plätze ergattert — dein Shooting kostet dich nichts.",
        });
        setPromo((p) => (p ? { ...p, remaining: Math.max(0, p.remaining - 1), claimed: p.claimed + 1 } : p));
      } else {
        toast.success("Anfrage erhalten — ich melde mich innerhalb von 24 Stunden.", {
          description: `${format(selected, "EEEE, d. MMMM yyyy", { locale: de })} · ${TYPE_LABELS[form.shoot_type]}`,
        });
      }
      setForm({ name: "", email: "", shoot_type: "hochzeit", time_slot: "flexibel", message: "" });
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const today = new Date();

  return (
    <section id="termine" data-testid="booking-section" className="border-t border-white/10 bg-ink2">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-12 lg:px-12 lg:py-40">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Termine — Verfügbarkeit prüfen</p>
          <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
            Ihr Datum, <span className="italic text-zinc-400">unser Anfang.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400">
            Wählen Sie Ihren Wunschtermin im Kalender. Durchgestrichene Tage sind bereits reserviert.
            Ihre Anfrage ist unverbindlich — ich bestätige die Verfügbarkeit persönlich.
          </p>
          {avail && (
            <p className="mt-6 flex w-fit items-center gap-3 border border-gold/40 bg-gold/5 px-5 py-3" data-testid="booking-availability-badge">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white">
                Noch <span className="text-gold">{avail.free} Termine</span> frei im {avail.label}
              </span>
            </p>
          )}

          <div className="mt-10 inline-block border border-white/10 bg-ink p-6" data-testid="booking-date-picker">
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={setSelected}
              locale={de}
              fromDate={today}
              disabled={[{ before: today }, ...bookedDates]}
              modifiers={{ booked: bookedDates }}
              modifiersClassNames={{ booked: "rdp-day_booked" }}
              showOutsideDays
            />
            <div className="mt-4 flex items-center gap-6 border-t border-white/10 pt-4">
              <span className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                <span className="h-2 w-2 rounded-full bg-gold" /> Ausgewählt
              </span>
              <span className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                <span className="font-mono text-terracotta line-through">15</span> Reserviert
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-7 lg:pl-10"
        >
          <form onSubmit={submit} className="border border-white/10 bg-ink p-8 lg:p-12" data-testid="booking-form">
            {promo && promo.active && promo.remaining > 0 && (
              <div className="mb-8 flex items-center gap-3 border border-gold/50 bg-gold/10 px-5 py-4" data-testid="booking-promo-note">
                <span className="font-serif text-xl text-gold">Geschenk!</span>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] leading-relaxed text-zinc-200">
                  Diese Anfrage sichert dir einen der <span className="text-gold">{promo.remaining} noch freien Gratis-Plätze</span> — dein Shooting kostet dich nichts.
                </p>
              </div>
            )}
            <div className="mb-8 flex items-center gap-4 border-b border-white/10 pb-6">
              <CalendarCheck size={20} className="text-gold" />
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Gewähltes Datum</p>
                <p className="mt-1 font-serif text-xl text-white" data-testid="booking-selected-date">
                  {selected ? format(selected, "EEEE, d. MMMM yyyy", { locale: de }) : "Noch kein Datum gewählt"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
              <div>
                <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Ihr Name *</label>
                <input
                  data-testid="booking-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Anna & Tom Beispiel"
                  className="input-line"
                />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">E-Mail *</label>
                <input
                  data-testid="booking-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="hallo@beispiel.de"
                  className="input-line"
                />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Art des Shootings *</label>
                <select
                  data-testid="booking-type-select"
                  value={form.shoot_type}
                  onChange={(e) => setForm({ ...form, shoot_type: e.target.value })}
                  className="input-line cursor-pointer"
                >
                  {BOOKING_TYPES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Tageszeit</label>
                <select
                  data-testid="booking-timeslot-select"
                  value={form.time_slot}
                  onChange={(e) => setForm({ ...form, time_slot: e.target.value })}
                  className="input-line cursor-pointer"
                >
                  {TIME_SLOTS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                  Erzählen Sie kurz von Ihrem Vorhaben
                </label>
                <textarea
                  data-testid="booking-message-input"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Location, Gästezahl, Stimmung, besondere Wünsche …"
                  className="input-line resize-none"
                />
              </div>
            </div>

            <button
              data-testid="booking-submit-button"
              type="submit"
              disabled={sending}
              className="mt-10 w-full rounded-full bg-gold py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white disabled:opacity-50"
            >
              {sending ? "Wird gesendet …" : "Termin unverbindlich anfragen"}
            </button>
            <p className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">
              Antwort innerhalb von 24 Stunden — versprochen.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
